import { useState } from 'react';
import { motion } from 'framer-motion';

interface DiscountData {
  code?: string;
  percent?: number;
  validUntilRemark?: string;
  firstPurchaseOnly?: boolean;
}

interface PromoCTAButtonBestFirmsProps {
  firmId: string;
  affiliateLink: string;
  originalFee: number;
  discountedFee?: number;
  discount?: DiscountData;
  isBestDeal?: boolean;
  size?: 'sm' | 'md';
}

export default function PromoCTAButtonBestFirms({
  firmId,
  originalFee,
  discountedFee,
  discount,
  isBestDeal = false,
  size = 'md',
}: PromoCTAButtonBestFirmsProps) {
  const [revealed, setRevealed] = useState(false);
  const isPromoActive = discount?.code && discount?.percent;
  const savings = originalFee - (discountedFee || 0);
  const link = `/go/${encodeURIComponent(firmId)}`;

  const handleReveal = () => {
    setRevealed(true);
  };

  const buttonBaseStyles =
    size === 'sm'
      ? "w-[120px] px-3 py-1.5 text-xs font-semibold rounded text-center duration-200 ease-in-out"
      : "w-[140px] px-4 py-2 text-sm font-semibold rounded text-center duration-200 ease-in-out";
  const primaryButtonStyles = isBestDeal
    ? `${buttonBaseStyles} text-black bg-yellow-400 hover:bg-yellow-300`
    : `${buttonBaseStyles} text-black bg-yellow-600 hover:bg-yellow-500`;
  const secondaryButtonStyles = `${buttonBaseStyles} text-white bg-gray-700 hover:bg-gray-600`;

  const title = discount?.validUntilRemark || (discount?.firstPurchaseOnly ? 'First purchase only' : undefined);

  return (
    <div className="flex flex-col items-start gap-1">
      {isPromoActive ? (
        <>
          <div className="flex items-center gap-2">
            {!revealed ? (
              <button
                onClick={handleReveal}
                title={title}
                aria-label={`Reveal promo code for ${firmId}`}
                className={primaryButtonStyles}
              >
                {discount.percent ? `Claim ${discount.percent}% OFF` : 'Claim Discount'}
              </button>
            ) : (
              <motion.a
                title={title}
                aria-label={`Use promo code ${discount.code}`}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2 }}
                className={primaryButtonStyles}
              >
                Use Code: {discount.code}
              </motion.a>
            )}
          </div>
          <span className="text-xs text-yellow-300 block">
            Save ${savings.toLocaleString()} now
          </span>
        </>
      ) : (
        <a
          aria-label={`Visit ${firmId} prop firm link`}
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={secondaryButtonStyles}
        >
          Get Funded
        </a>
      )}
    </div>
  );
}
